import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Clock, FileCheck, Layers, Lock, ReceiptText, ShieldCheck } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import Seo, { BREADCRUMB_JSONLD, FAQ_JSONLD, SERVICE_JSONLD, SITE_URL } from "@/components/Seo";
import { api, fmtMoney } from "@/lib/api";

// The service page for jewellers searching for somewhere to send CAD work
// ("jewellery CAD outsourcing", "outsource jewellery CAD design"). Every claim
// here restates something already published on the site — the homepage FAQ, the
// revision policy on /how-it-works, and the supplier checklist in the journal —
// so this page and those cannot disagree. File rights follow the Terms wording
// ("for the purpose commissioned"), not a broader promise.
const PATH = "/jewellery-cad-outsourcing";

const LINK = "text-foreground underline underline-offset-4 decoration-primary/50 hover:text-primary transition-colors";

const YOU_SEND = [
  "A sketch, a photograph, a sample or a written brief. Whatever you have is a starting point.",
  "The measurements that matter: finger size, stone dimensions in millimetres, and the metal.",
  "The files your caster or manufacturer needs, and the date you need them by.",
];

const YOU_RECEIVE = [
  "One fixed-price quotation with a delivery estimate, before you commit to anything.",
  "A 3D model built for manufacture by a vetted jewellery CAD specialist.",
  "Up to two revisions on the approved brief, unless your quotation says otherwise.",
  "Production-ready files in the formats agreed: typically STL for printing and casting, with STEP and OBJ where relevant.",
];

const REASONS = [
  { icon: ShieldCheck, title: "One accountable studio", body: "You contract with GetJewelDesigns, not with an individual freelancer. If a specialist cannot finish a job, that is our problem to solve, not yours." },
  { icon: Lock, title: "Your customer stays yours", body: "Clients and specialists never see each other. Briefs and messages are routed through the studio, and specialists agree not to contact clients outside it." },
  { icon: ReceiptText, title: "A price before you quote", body: "Quotations are free, and the price on them is fixed. Get the design cost first, then price the piece for your customer with your margin intact." },
  { icon: FileCheck, title: "Files built for production", body: "Models are prepared for printing and casting, and delivered in the formats your quotation names." },
];

const STEPS = [
  { title: "Send the brief", body: "Choose a category, upload your references and describe the piece. Submitting a brief is free." },
  { title: "Approve the quotation", body: "We reply with one fixed price and a delivery estimate. Work begins only once you approve and pay." },
  { title: "Review and revise", body: "Preview the model, gather your customer's comments, and use your included revisions before you approve." },
  { title: "Download the files", body: "Approve the final work to unlock your production files." },
];

const FAQS = [
  { q: "What is jewellery CAD outsourcing?", a: "Jewellery CAD outsourcing means sending a design brief, such as a sketch, photograph or sample, to an outside studio that builds the 3D model and returns production-ready files, instead of employing a CAD designer in-house. You pay per design rather than carrying a salary." },
  { q: "How much does it cost to outsource jewellery CAD design?", a: "Each design is quoted individually at one fixed price, based on the category, the complexity and the files you need. Quotations are free and carry no obligation, and current starting prices for each category are listed on our categories page." },
  { q: "How long does an outsourced CAD design take?", a: "Most categories deliver an initial draft within 5–7 days. Your quotation shows the delivery estimate before you commit." },
  { q: "What files do I receive?", a: "Production-ready files in the formats named in your quotation: typically STL for printing and casting, with STEP and OBJ available where relevant. Renders for customer approval can be added to the same quotation." },
  { q: "What can I do with the delivered files?", a: "Once an order is paid and you approve the final work, you receive the delivered production files and the right to use them for the purpose commissioned. If you plan a broader use, such as a stock line or variants, say so at quotation stage and we will agree it explicitly." },
  { q: "Is my customer's design kept confidential?", a: "Yes. Clients and specialists never see each other. Briefs, messages and files are routed and moderated through the studio, and specialists agree not to contact or identify clients outside it." },
  { q: "Who does the work?", a: "A vetted jewellery CAD specialist working under GetJewelDesigns' Design Team. You deal only with the studio, and the studio is responsible to you for the price, the quality and the delivery." },
  { q: "Do you work with jewellers in the UK and other countries?", a: "Yes. The studio is based in Surat, India, and everything from the brief to the final download is handled online, so we take briefs from jewellers in any country, including the UK and the US." },
];

const OUTSOURCING_JSONLD = {
  ...SERVICE_JSONLD,
  name: "Jewellery CAD outsourcing",
  url: `${SITE_URL}${PATH}`,
};

export default function Outsourcing() {
  const [cats, setCats] = useState([]);
  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);

  return (
    <div>
      <Seo
        title="Jewellery CAD outsourcing services"
        description="Outsource jewellery CAD design to a managed studio. Send a sketch, photo or sample and get production-ready 3D files at one fixed price. Quotations are free."
        path={PATH}
        jsonLd={[
          OUTSOURCING_JSONLD,
          BREADCRUMB_JSONLD([{ name: "Home", path: "/" }, { name: "Jewellery CAD outsourcing", path: PATH }]),
          FAQ_JSONLD(FAQS),
        ]}
      />

      {/* Hero */}
      <section className="border-b hairline">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8 py-16 md:py-28">
          <div className="text-xs uppercase tracking-[0.3em] text-primary mb-6">3D jewellery CAD design services</div>
          <h1 className="font-serif text-4xl md:text-6xl leading-[1.05] mb-8 tracking-tight max-w-4xl">
            Outsource your <em className="text-primary not-italic">jewellery CAD design</em> to a managed studio.
          </h1>
          <p className="text-muted-foreground text-lg leading-relaxed max-w-3xl mb-10">
            GetJewelDesigns is a jewellery CAD outsourcing service for retailers, manufacturers and independent
            jewellers. Send a sketch, a photograph or a sample. We quote one fixed price, assign a vetted CAD
            specialist, and deliver production-ready 3D files, so you can take on bespoke work without hiring,
            vetting or managing a designer.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link
              to="/categories"
              data-testid="outsourcing-cta-quote"
              className="inline-flex items-center gap-3 border border-primary text-primary px-8 py-4 text-xs uppercase tracking-widest hover:bg-primary hover:text-primary-foreground transition-colors duration-300"
            >
              Get a free quotation <ArrowUpRight className="h-4 w-4" strokeWidth={1} />
            </Link>
            <Link
              to="/how-it-works"
              className="inline-flex items-center gap-3 border hairline px-8 py-4 text-xs uppercase tracking-widest hover:border-primary/60 transition-colors duration-300"
            >
              How it works
            </Link>
          </div>
          <div className="mt-14 flex flex-wrap gap-10 text-xs uppercase tracking-widest text-muted-foreground">
            <div className="flex items-center gap-3"><ReceiptText className="h-4 w-4 text-primary" strokeWidth={1} /> Free, fixed-price quotation</div>
            <div className="flex items-center gap-3"><Clock className="h-4 w-4 text-primary" strokeWidth={1} /> First draft in 5–7 days</div>
            <div className="flex items-center gap-3"><Layers className="h-4 w-4 text-primary" strokeWidth={1} /> STL · STEP · OBJ</div>
          </div>
        </div>
      </section>

      {/* What you send, what you receive */}
      <section className="border-b hairline">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8 py-16 md:py-24">
          <h2 className="font-serif text-3xl md:text-5xl mb-6">What outsourcing your CAD looks like.</h2>
          <p className="text-muted-foreground max-w-3xl leading-relaxed mb-12">
            You stay with your customer and your bench. The modelling leaves your building as a brief and comes
            back as files, whether you start from{" "}
            <Link to="/blog/sketch-to-cad-jewellery-design" className={LINK}>a sketch</Link> or from{" "}
            <Link to="/blog/jewellery-cad-from-a-photo-or-sample" className={LINK}>a photo or a sample</Link>.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-border border hairline">
            <div className="bg-background p-8">
              <h3 className="font-serif text-2xl mb-5">You send</h3>
              <ul className="space-y-4 text-muted-foreground leading-relaxed list-disc pl-5 marker:text-primary">
                {YOU_SEND.map((item) => <li key={item}>{item}</li>)}
              </ul>
            </div>
            <div className="bg-background p-8">
              <h3 className="font-serif text-2xl mb-5">You receive</h3>
              <ul className="space-y-4 text-muted-foreground leading-relaxed list-disc pl-5 marker:text-primary">
                {YOU_RECEIVE.map((item) => <li key={item}>{item}</li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Why a studio */}
      <section className="border-b hairline">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8 py-16 md:py-24">
          <h2 className="font-serif text-3xl md:text-5xl mb-12">Why jewellers outsource CAD to a studio.</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-border border hairline">
            {REASONS.map((r) => (
              <div key={r.title} className="bg-background p-8">
                <r.icon className="h-6 w-6 text-primary mb-5" strokeWidth={1} />
                <h3 className="font-serif text-2xl mb-3">{r.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{r.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cost. Prices come from the same API the category pages read, so they
          cannot drift from what a client is actually quoted from. With the API
          unreachable the list is simply absent and the links below still work. */}
      <section className="border-b hairline">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8 py-16 md:py-24">
          <h2 className="font-serif text-3xl md:text-5xl mb-6">What outsourced CAD costs.</h2>
          <p className="text-muted-foreground max-w-3xl leading-relaxed mb-10">
            Every design is quoted individually, at one fixed price, before any work starts. Stone count, setting
            style and extra deliverables move the number, and{" "}
            <Link to="/blog/how-much-does-jewellery-cad-design-cost" className={LINK}>our guide to jewellery CAD design cost</Link>{" "}
            sets out how. Starting prices are listed by category.
          </p>
          {cats.length > 0 && (
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
              {cats.map((c) => (
                <li key={c.id}>
                  <Link to={`/categories/${c.slug}`} data-testid={`outsourcing-cat-${c.slug}`} className="group block border hairline p-6 hover:border-primary/60 transition-colors duration-300">
                    <div className="font-serif text-2xl mb-2 group-hover:text-primary transition-colors duration-300">{c.name} CAD design</div>
                    <div className="text-xs uppercase tracking-[0.25em] text-primary">From {fmtMoney(c.starting_price, c.currency)}{c.starting_price_usd ? ` · ${fmtMoney(c.starting_price_usd, "USD")}` : ""}</div>
                    <div className="mt-3 text-xs uppercase tracking-widest text-muted-foreground">{c.turnaround_days} day{c.turnaround_days > 1 ? "s" : ""} · {c.included_revisions} revisions</div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
          <Link to="/categories" className="text-xs uppercase tracking-widest text-muted-foreground hover:text-primary transition-colors duration-300">See all categories and prices</Link>
        </div>
      </section>

      {/* Process */}
      <section className="border-b hairline">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8 py-16 md:py-24">
          <div className="flex items-end justify-between mb-10 md:mb-12 flex-wrap gap-3">
            <h2 className="font-serif text-3xl md:text-5xl">From brief to files in four steps.</h2>
            <Link to="/how-it-works" className="text-xs uppercase tracking-widest text-muted-foreground hover:text-primary transition-colors duration-300">See full process</Link>
          </div>
          <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((s, i) => (
              <li key={s.title} className="border hairline p-8">
                <div className="font-serif text-4xl text-primary/30 mb-6">{String(i + 1).padStart(2, "0")}</div>
                <h3 className="font-serif text-2xl mb-3">{s.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Outsource, hire or freelance */}
      <section className="border-b hairline">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8 py-16 md:py-24">
          <h2 className="font-serif text-3xl md:text-5xl mb-6">Studio, freelancer or in-house?</h2>
          <p className="text-muted-foreground max-w-3xl leading-relaxed mb-6">
            Outsourcing is not always the right answer. A designer on staff makes sense when the work is steady
            enough to keep them busy. A freelancer is usually the lowest rate, if you have the time to find, vet
            and manage one. A managed studio fits when bespoke work is valuable but uneven: enough to need
            reliable CAD, not enough to justify a salary.
          </p>
          <p className="text-muted-foreground max-w-3xl leading-relaxed">
            We set out the honest case for each in{" "}
            <Link to="/blog/in-house-freelancer-or-managed-studio-jewellery-cad" className={LINK}>in-house, freelancer or managed studio</Link>,
            and the questions worth asking any supplier, including us, in{" "}
            <Link to="/blog/what-to-check-before-you-outsource-jewellery-cad" className={LINK}>what to check before you outsource jewellery CAD</Link>.
          </p>
        </div>
      </section>

      {/* FAQ. Answers start open so they ship in the prerendered HTML, as on
          the homepage (see the note in Landing.jsx). */}
      <section className="border-b hairline">
        <div className="max-w-[900px] mx-auto px-4 md:px-8 py-16 md:py-24">
          <div className="text-xs uppercase tracking-[0.3em] text-primary mb-4 text-center">Questions</div>
          <h2 className="font-serif text-3xl md:text-5xl mb-10 md:mb-14 text-center">Outsourcing jewellery CAD, answered.</h2>
          <Accordion type="multiple" defaultValue={FAQS.map((_, i) => `faq-${i}`)} className="w-full">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-b hairline">
                <AccordionTrigger className="text-left font-serif text-xl md:text-2xl hover:no-underline py-6">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base leading-relaxed pb-6">
                  <p>{f.a}</p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Closing CTA */}
      <section>
        <div className="max-w-[1200px] mx-auto px-4 md:px-8 py-16 md:py-24 text-center">
          <h2 className="font-serif text-3xl md:text-5xl mb-6">Have a commission waiting?</h2>
          <p className="text-muted-foreground max-w-xl mx-auto mb-10">
            Send the brief and have a fixed price before you quote your customer. There is no obligation until
            you approve.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/categories" className="inline-flex items-center gap-3 border border-primary text-primary px-8 py-4 text-xs uppercase tracking-widest hover:bg-primary hover:text-primary-foreground transition-colors duration-300">
              Get a free quotation <ArrowUpRight className="h-4 w-4" strokeWidth={1} />
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-3 border hairline px-8 py-4 text-xs uppercase tracking-widest hover:border-primary/60 transition-colors duration-300">
              Contact the studio
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
